(() => {
  'use strict';
  const controllers = new Map();
  const controlDisabled = new WeakMap();
  const boundGalleries = new WeakSet();
  const boundDrawers = new WeakSet();
  const rootUrl = () => window.Shopify?.routes?.root || '/';
  const setText = (element, value) => {
    if (element && element.textContent !== value) element.textContent = value;
  };
  const format = (cents, currency) => new Intl.NumberFormat(document.documentElement.lang || 'en', {
    style: 'currency', currency
  }).format(cents / 100);

  function setInactive(container, inactive) {
    if (!container) return;
    container.hidden = inactive;
    container.inert = inactive;
    container.setAttribute('aria-hidden', String(inactive));
    container.querySelectorAll('input, select, button').forEach(control => {
      if (inactive) {
        if (!controlDisabled.has(control)) controlDisabled.set(control, control.disabled);
        control.disabled = true;
      } else if (controlDisabled.has(control)) {
        control.disabled = controlDisabled.get(control);
        controlDisabled.delete(control);
      }
    });
  }

  function chooseMedia(gallery, mediaId) {
    if (!gallery.querySelector(`[data-media-id="${CSS.escape(mediaId)}"][data-dj-variant]`)) return;
    gallery.dataset.djSelectedMedia = mediaId;
    gallery.querySelectorAll('[data-dj-variant]').forEach(element => {
      element.toggleAttribute('data-dj-selected', (element.dataset.mediaId || element.dataset.target) === mediaId);
    });
  }

  function bindGallery(gallery) {
    if (boundGalleries.has(gallery) || typeof gallery.setActiveMedia !== 'function') return;
    boundGalleries.add(gallery);
    const nativeSetActiveMedia = gallery.setActiveMedia;
    gallery.setActiveMedia = function (id, ...args) {
      chooseMedia(this, id);
      return nativeSetActiveMedia.call(this, id, ...args);
    };
  }

  class DialJoyPurchase {
    constructor(info, config) {
      this.info = info;
      this.config = config;
      this.section = info.dataset.section;
      this.quantity = info.querySelector('quantity-breaks[data-dj-owned="true"]');
      this.primary = info.querySelector('variant-selects, variant-radios');
      this.form = info.querySelector('product-form[data-main="true"] form') || info.querySelector('product-form form');
      this.gallery = document.getElementById(`MediaGallery-${this.section}`);
      this.sticky = document.getElementById(`sticky-atc-${this.section}`);
      this.button = document.getElementById(`ProductSubmitButton-${this.section}`);
      this.packRows = Object.fromEntries(['2', '3'].map(size => [size, Array.from(this.quantity.querySelectorAll(`label[data-quantity="${size}"] .quantity-break__selector-item`))]));
      this.rows = Object.values(this.packRows).flat();
      this.variants = JSON.parse(this.quantity.querySelector('script[type="application/json"]').textContent);
      const initial = this.form.querySelector('input[name="id"]').value;
      this.state = {singleId: String(initial), packIds: Object.fromEntries(Object.entries(this.packRows).map(([size, rows]) => [size, rows.map(row => String(row.dataset.selectedId))])), mode: '1', preview: 0};
      this.summary = document.createElement('div');
      this.summary.className = 'dj-selection-summary';
      this.summary.setAttribute('role', 'status');
      this.summary.setAttribute('aria-live', 'polite');
      this.summary.hidden = true;
      this.quantity.after(this.summary);
      this.note = document.createElement('p');
      this.note.className = 'dj-discount-note';
      this.note.textContent = config.discountNote.replace('__AMOUNT__', format(config.discountCents, config.currency));
      this.summary.after(this.note);
      this.quantity.querySelectorAll('input[name="quantity"]').forEach(input => {
        input.setAttribute('aria-label', input.value === '3' ? config.trio : input.value === '2' ? config.pair : config.single);
      });
      this.rows.forEach(row => {
        const size = row.closest('label[data-quantity]').dataset.quantity;
        const i = this.packRows[size].indexOf(row);
        row.querySelectorAll('select').forEach((select, j) => {
          select.setAttribute('aria-label', (j === 0 ? config.color : config.connection).replace('__NUMBER__', String(i + 1)));
        });
        setText(row.querySelector('.quantity-break__selector-item__number'), config.handset.replace('__NUMBER__', String(i + 1)));
        row.addEventListener('focusin', () => {
          if (this.state.mode !== size) return;
          this.state.preview = i;
          this.renderMedia();
        });
      });
      this.info.addEventListener('change', event => this.onChange(event));
      document.addEventListener('click', event => {
        const link = event.target.closest('[data-dj-select-pack]');
        if (!link || !this.config.pairEnabled) return;
        const input = this.quantity.querySelector(`input[name="quantity"][value="${link.dataset.djSelectPack}"]`);
        if (input && !input.disabled) input.click();
      });
      this.info.addEventListener('change', event => {
        if (this.state.mode !== '1' && this.primary?.contains(event.target)) {
          event.stopImmediatePropagation();
          event.preventDefault();
        }
      }, true);
      this.quantity.querySelectorAll('input[name="quantity"]').forEach(input => {
        input.checked = input.value === '1';
      });
      if (this.button) new MutationObserver(() => this.render(false)).observe(this.button, {
        attributes: true, attributeFilter: ['disabled', 'aria-disabled']
      });
      this.render(true);
      window.addEventListener('scroll', () => this.renderStickyVisibility(), {passive: true});
      window.addEventListener('resize', () => this.renderStickyVisibility(), {passive: true});
      window.addEventListener('pageshow', () => this.render(false));
    }

    variant(id) {
      return this.variants.find(variant => String(variant.id) === String(id));
    }

    discountForSize(size) {
      return Number(size) >= 3 ? this.config.trioDiscountCents : Number(size) === 2 ? this.config.discountCents : 0;
    }

    onChange(event) {
      const row = event.target.closest('.quantity-break__selector-item');
      if (row && this.rows.includes(row)) {
        const size = row.closest('label[data-quantity]').dataset.quantity;
        const index = this.packRows[size].indexOf(row);
        const options = Array.from(row.querySelectorAll('select'), select => select.value);
        const variant = this.variants.find(value => value.options.every((option, i) => option === options[i]));
        if (variant) this.state.packIds[size][index] = String(variant.id);
        this.state.preview = index;
      } else if (event.target.name === 'quantity' && this.quantity.contains(event.target)) {
        this.state.mode = event.target.value;
        this.state.preview = 0;
      } else if (this.primary?.contains(event.target)) {
        this.state.singleId = String(this.form.querySelector('input[name="id"]').value);
      } else return;
      this.render(true);
    }

    renderMedia() {
      const variant = this.variant(this.state.mode !== '1' ? this.state.packIds[this.state.mode][this.state.preview] : this.state.singleId);
      if (!variant?.featured_media || !this.gallery) return;
      bindGallery(this.gallery);
      const id = `${this.section}-${variant.featured_media.id}`;
      if (typeof this.gallery.setActiveMedia === 'function') this.gallery.setActiveMedia(id, false);
      else chooseMedia(this.gallery, id);
      const label = this.gallery.querySelector('[data-dj-preview-label]');
      if (label) {
        label.hidden = this.state.mode === '1';
        setText(label, this.config.preview.replace('__NUMBER__', String(this.state.preview + 1)));
      }
      this.gallery.querySelectorAll(`[data-media-id="${CSS.escape(id)}"] img, [data-target="${CSS.escape(id)}"] img`).forEach(img => {
        img.alt = variant.title;
      });
    }

    render(refreshMedia) {
      const pair = ['2', '3'].includes(this.state.mode) && this.config.pairEnabled && this.packRows[this.state.mode].length === Number(this.state.mode);
      this.state.mode = pair ? this.state.mode : '1';
      setInactive(this.primary, pair);
      Object.entries(this.packRows).forEach(([size, rows]) => {
        rows.forEach(row => setInactive(row, this.state.mode !== size));
        const input = this.quantity.querySelector(`input[name="quantity"][value="${size}"]`);
        const label = this.quantity.querySelector(`label[data-quantity="${size}"]`);
        if (input) { input.disabled = !this.config.pairEnabled; input.hidden = !this.config.pairEnabled; }
        if (label) label.hidden = !this.config.pairEnabled;
        this.quantity.selectedVariants[`input_${size}`] = [...this.state.packIds[size]];
      });
      this.quantity.querySelectorAll('input[name="quantity"]').forEach(input => {
        input.checked = input.value === this.state.mode;
      });
      this.info.classList.toggle('dj-pair-mode', pair);
      this.quantity.selectedQuantity = Number(this.state.mode);
      this.quantity.formVariants = pair ? [...this.state.packIds[this.state.mode]] : [];
      const chosen = pair ? this.state.packIds[this.state.mode].map(id => this.variant(id)) : [this.variant(this.state.singleId)];
      const single = this.variant(this.state.singleId);
      if (chosen.some(variant => !variant)) return;
      const discount = pair ? this.discountForSize(this.state.mode) : 0;
      const total = chosen.reduce((sum, variant) => sum + variant.price, 0) - discount;
      const money = format(total, this.config.currency);
      const action = (this.state.mode === '3' ? this.config.addTrio : pair ? this.config.addPair : this.config.addSingle).replace('__PRICE__', money);
      this.quantity.querySelectorAll('label[data-quantity]').forEach(label => {
        const ids = this.state.packIds[label.dataset.quantity];
        const price = ids?.length ? ids.reduce((sum, id) => sum + this.variant(id).price, 0) - this.discountForSize(label.dataset.quantity) : single.price;
        setText(label.querySelector('.quantity-break__price'), format(price, this.config.currency));
        if (ids?.length) {
          const original = price + this.discountForSize(label.dataset.quantity);
          const compare = label.querySelector('.quantity-break__compare-price');
          setText(compare, format(original, this.config.currency));
          compare?.setAttribute('aria-label', this.config.separately.replace('__PRICE__', format(original, this.config.currency)));
        }
      });
      const priceElement = document.getElementById(`price-${this.section}`);
      const priceKey = `${this.state.mode}:${total}`;
      if (priceElement && (priceElement.dataset.djPriceKey !== priceKey || priceElement.querySelector('.dj-current-total')?.textContent !== money)) {
        const current = Object.assign(document.createElement('span'), {className: 'dj-current-total', textContent: money});
        const elements = [current];
        if (pair) {
          const original = format(total + discount, this.config.currency);
          const compare = Object.assign(document.createElement('s'), {className: 'dj-set-original', textContent: original});
          compare.setAttribute('aria-label', this.config.separately.replace('__PRICE__', original));
          const saving = Object.assign(document.createElement('span'), {className: 'dj-set-saving', textContent: this.config.save.replace('__AMOUNT__', format(discount, this.config.currency))});
          elements.push(compare, saving);
        }
        priceElement.replaceChildren(...elements);
        priceElement.dataset.djPriceKey = priceKey;
      }
      if (!this.button?.disabled) setText(this.button?.querySelector('.main-atc__label__text'), action);
      setText(this.sticky?.querySelector('.sticky-atc__label__text'), action);
      setText(this.sticky?.querySelector('.sticky-atc__title'), this.state.mode === '3' ? this.config.trio : pair ? this.config.pair : single.title);
      if (pair) {
        const text = chosen.map((variant, i) => `${this.config.handset.replace('__NUMBER__', String(i + 1))}: ${variant.title}`).join('\n');
        setText(this.summary, text);
      }
      this.summary.hidden = !pair;
      this.note.hidden = !pair;
      if (pair) setText(this.note, this.config.discountNote.replace('__AMOUNT__', format(discount, this.config.currency)));
      if (refreshMedia) this.renderMedia();
      this.renderStickyVisibility();
    }

    renderStickyVisibility() {
      if (!this.sticky || !this.button) return;
      const open = document.querySelector('cart-drawer.active');
      const visible = !open && this.button.getBoundingClientRect().bottom < 0;
      this.sticky.dataset.djStickyVisible = String(visible);
      this.sticky.hidden = !visible;
      this.sticky.inert = !visible;
    }
  }

  function initCart() {
    document.querySelectorAll('cart-drawer').forEach(drawer => {
      if (boundDrawers.has(drawer)) return;
      boundDrawers.add(drawer);
      new MutationObserver(() => controllers.forEach(controller => controller.renderStickyVisibility())).observe(drawer, {
        attributes: true, attributeFilter: ['class']
      });
    });
    const link = document.getElementById('cart-icon-bubble');
    if (link && !link.dataset.djCountBound) {
      link.dataset.djCountBound = 'true';
      const label = link.getAttribute('aria-label') || 'Cart, 0 items';
      const update = () => {
        const count = link.querySelector('[data-dj-cart-count]')?.dataset.djCartCount;
        if (count !== undefined) link.setAttribute('aria-label', label.replace(/\d+/, count));
      };
      new MutationObserver(update).observe(link, {childList: true, subtree: true});
      update();
    }
  }

  function init(root = document) {
    root.querySelectorAll('.dj-smart-gallery').forEach(gallery => {
      if (typeof gallery.setActiveMedia === 'function') bindGallery(gallery);
      else customElements.whenDefined('media-gallery').then(() => bindGallery(gallery));
    });
    root.querySelectorAll('product-info').forEach(info => {
      const data = info.querySelector('[data-dj-offer-config]');
      if (!data || !info.querySelector('quantity-breaks[data-dj-owned="true"]') || controllers.has(info.dataset.section)) return;
      controllers.set(info.dataset.section, new DialJoyPurchase(info, JSON.parse(data.textContent)));
    });
    initCart();
  }

  document.addEventListener('change', event => {
    const select = event.target.closest('[data-dj-upsell-variant]');
    if (!select) return;
    const upsell = select.closest('[data-dj-upsell]');
    const option = select.selectedOptions[0];
    const image = upsell.querySelector('img');
    if (image && option.dataset.image) {
      image.removeAttribute('srcset');
      image.src = option.dataset.image;
      image.alt = option.textContent.trim();
    }
    const discount = Number(upsell.dataset.discountCents);
    const firstPrice = Number(upsell.dataset.firstPrice);
    const secondPrice = Number(option.dataset.price);
    const text = upsell.dataset.detail.replace('__INCREMENT__', format(secondPrice - discount, upsell.dataset.currency))
      .replace('__TOTAL__', format(firstPrice + secondPrice - discount, upsell.dataset.currency));
    setText(upsell.querySelector('[data-dj-upsell-detail]'), text);
  });

  document.addEventListener('submit', async event => {
    const form = event.target.closest('[data-dj-upsell-form]');
    if (!form) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (form.dataset.busy === 'true') return;
    const id = Number(form.elements.id.value);
    if (!Number.isSafeInteger(id) || id <= 0) return;
    const upsell = form.closest('[data-dj-upsell]');
    const button = form.querySelector('button[type="submit"]');
    const error = form.querySelector('[role="alert"]');
    const cartLink = form.querySelector('[data-dj-check-cart]');
    const original = button.textContent;
    form.dataset.busy = 'true';
    button.disabled = true;
    button.textContent = form.dataset.adding;
    error.hidden = true;
    cartLink.hidden = true;
    let confirmedFailure = false;
    let confirmedSuccess = false;
    try {
      const drawer = document.querySelector('cart-drawer');
      const sections = drawer?.getSectionsToRender?.().map(section => section.id).filter(Boolean).slice(0, 5) || [];
      const payload = {items: [{id, quantity: 1}]};
      if (sections.length) Object.assign(payload, {sections, sections_url: location.pathname});
      const response = await fetch(rootUrl() + 'cart/add.js', {
        method: 'POST', headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
        credentials: 'same-origin', body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        confirmedFailure = true;
        throw new Error(data.description || data.message || form.dataset.error);
      }
      confirmedSuccess = true;
      if (upsell.dataset.surface === 'cart') {
        location.assign(rootUrl() + 'cart');
        return;
      }
      if (drawer && sections.length && typeof drawer.renderContents === 'function') {
        drawer.setActiveElement?.(document.getElementById('cart-icon-bubble'));
        drawer.renderContents(data);
        drawer.classList.remove('is-empty');
      } else location.assign(rootUrl() + 'cart');
    } catch (failure) {
      if (confirmedSuccess) {
        location.assign(rootUrl() + 'cart');
        return;
      }
      error.textContent = confirmedFailure ? failure.message : form.dataset.uncertain;
      error.hidden = false;
      cartLink.hidden = confirmedFailure;
    } finally {
      if (!confirmedSuccess) {
        delete form.dataset.busy;
        button.disabled = false;
        button.textContent = original;
      }
    }
  }, true);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, event => controllers.get(event.data?.sectionId)?.render(false));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', event => {
    event.target.querySelectorAll('product-info').forEach(info => controllers.delete(info.dataset.section));
    init(event.target);
  });
})();
